<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue">
          <graph-tool-bar :showSource="false" granularity="day" @on-selected="onPanelSelect"></graph-tool-bar>
        </md-card-header>

        <md-card-content>
          <highcharts class="charts" :options="chartOptions" ref="chart"></highcharts>
          <br />
          <div class="modal-footer">
            <md-button
              v-show="interval === 0"
              class="md-success"
              @click.native="buttonSelect"
            >Refresh</md-button>
            <md-button v-show="interval > 0" disabled>Refresh ({{interval}})</md-button>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <snack-bar ref="snackbar"></snack-bar>
  </div>
</template>


<script>
import graphToolBar from "@/components/GraphToolBar";
import areaChartOptions from "@/config/charts/area";
import axios from "axios";
import { getSelectedDeviceToken } from "@/storage/memoryStorage";
import snackBar from "@/components/SnackBar";

export default {
  components: {
    "graph-tool-bar": graphToolBar,
    "snack-bar": snackBar
  },
  data() {
    return {
      chartOptions: null,
      permissions: 2,
      selected: {},
      interval: 0,
      intervalTimer: null,
      source: null
    };
  },

  created() {
    this.chartOptions = {
      chart: {
        polar: false,
        zoomType: null
      },
      title: {
        text: ""
      },
      subtitle: {
        text: ""
      },
      xAxis: {
        labels: {
          style: {
            fontSize: "12px"
          }
        },
        scrollbar: {
          enabled: false
        }
      },
      yAxis: {
        min: 0,
        max: 100,
        title: {
          text: ""
        },
        labels: {
          format: "{value}",
          style: {
            fontSize: "12px"
          }
        },
        type: "linear"
      },
      series: [],
      tooltip: {
        style: {
          whiteSpace: "nowrap",
          fontSize: "14px"
        },
        pointFormat:
          "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y}</b><br/>",
        headerFormat: ""
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: [
              "viewFullscreen",
              "printChart",
              "separator",
              "downloadCSV",
              "downloadXLS",
              "downloadPNG",
              "downloadPDF",
              "viewData"
            ]
          }
        }
      },
      credits: {
        enabled: false
      },
      colors: [
        "#00bcd4",
        "#f44336",
        "#4caf50",
        "#ff9800",
        "#fb4579",
        "#9761c8",
        "#4b63ec",
        "#fb636a",
        "#206692",
        "#668891"
      ],
      legend: {
        enabled: false,
        verticalAlign: "top"
      },
      lang: {
        decimalPoint: "."
      },
      navigator: {
        enabled: false
      },
      scrollbar: {
        enabled: true,
        liveRedraw: true,
        showFull: false
      },
      plotOptions: {
        pointStart: 1,
        series: {
          animation: false
        }
      }
    };

    this.chartOptions.title.text = "Harmonics chart";
    this.chartOptions.yAxis.labels.format = "{value} %";
    this.chartOptions.tooltip.pointFormat =
      "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} %</b><br/>";
    this.chartOptions.series = [
      {
        name: "L1 voltage",
        type: "column",
        marker: {
          enabled: false
        },
        dashStyle: "Solid",
        data: []
      },
      {
        name: "L1 current",
        type: "column",
        marker: {
          enabled: false
        },
        dashStyle: "Solid",
        data: []
      },
      {
        name: "L2 voltage",
        type: "column",
        marker: {
          enabled: false
        },
        dashStyle: "Solid",
        data: []
      },
      {
        name: "L2 current",
        type: "column",
        marker: {
          enabled: false
        },
        dashStyle: "Solid",
        data: []
      },
      {
        name: "L3 voltage",
        type: "column",
        marker: {
          enabled: false
        },
        dashStyle: "Solid",
        data: []
      },
      {
        name: "L3 current",
        type: "column",
        marker: {
          enabled: false
        },
        dashStyle: "Solid",
        data: []
      }
    ];

    this.chartOptions.chart.events = {
      redraw: function(event) {
        this.hideLoading();
      }
    };
  },

  mounted() {
    const { chart } = this.$refs.chart;
    chart.showLoading();

    let chartPermissionsList = {};

    if (this.permissions >= 1) {
      chartPermissionsList.legend = {
        enabled: true
      };
    }

    if (this.permissions >= 2) {
      chartPermissionsList.chart = {
        zoomType: "x"
      };
    }

    chart.update(chartPermissionsList);
  },

  methods: {
    buttonSelect() {
      if (this.interval == 0) {
        this.getAPIData();
        this.interval = 60;

        this.intervalTimer = setInterval(() => {
          if (this.interval == 0) {
            clearInterval(this.intervalTimer);
          } else {
            this.interval--;
          }
        }, 1000);
      } else {
      }
    },

    onPanelSelect(data) {
      this.selected = data;
      this.$nextTick(() => {
        this.buttonSelect();
      });
    },

    getAPIData() {
      const chartRef = this.$refs.chart.chart;
      chartRef.showLoading();

      this.source = axios.CancelToken.source();

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/applied-precision/harmonics",
        data: {
          device_token: getSelectedDeviceToken(this.selected.device.mac)
        },
        cancelToken: this.source.token
      })
        .then(response => {
          chartRef.series[0].setData(response.data.data.U.L1.values);
          chartRef.series[1].setData(response.data.data.I.L1.values);
          chartRef.series[2].setData(response.data.data.U.L2.values);
          chartRef.series[3].setData(response.data.data.I.L2.values);
          chartRef.series[4].setData(response.data.data.U.L3.values);
          chartRef.series[5].setData(response.data.data.I.L3.values);

          chartRef.xAxis[0].setExtremes(null, null, true);
        })
        .catch(error => {
          chartRef.hideLoading();
          if (error.response) {
            if (error.response.data.code == 604) {
              this.$refs.snackbar.notify(
                this.$lang.messages.device_not_responding_or_offline
              );
            } else {
              this.$refs.snackbar.notify(
                this.$lang.messages.api_communication_error
              );
            }
          }
        });
    }
  },

  beforeDestroy() {
    this.source.cancel();
  }
};
</script>

<style lang="scss" scoped>
.md-layout {
  margin-top: -29px;
}
</style>